// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';

import BusComponent from '../BusComponent';
import AreaTypeSelector from './AreaTypeSelector';
import AddNewCustomAnalysisValue from './AddNewCustomAnalysisValue';
import LocationAnalysisValueButton from './LocationAnalysisValueButton';
import LocationAnalysisType from '../../enums/LocationAnalysisType';
import AreaSelectorInfoPopover from './AreaSelectorInfoPopover';

export const SELECTION_LIMIT = 3;

/**
 * @typedef Props
 * @property {import('../../').LocationAnalysisType | undefined} analysisType
 * @property {string} mapInstanceId
 * @property {Function} onClose
 * @property {import('react-intl').intlShape} intl
 *
 * @typedef State
 * @property {import('../../').LocationAnalysisType | undefined} currentAreaType
 * @property {Set<number>} currentSelection
 * @property {any[]} customValues
 *
 * @extends {BusComponent<Props, State>}
 */
class AreaSelectorPopup extends BusComponent {
    constructor(props, context) {
        super(props, context);
        /** @type {State} */
        this.state = {
            currentAreaType: this.props.analysisType,
            currentSelection: new Set(),
            customValues: [],
        };
    }

    componentDidMount() {
        this.bindGluBusEvents({
            CUSTOM_LOCATION_ANALYSIS_VALUES: this.onCustomLocationAnalysisValues,
        });
        this.emit('CUSTOM_LOCATION_ANALYSIS_VALUES_REQUEST');
    }

    componentWillUnmount() {
        this.unbindGluBusEvents();
    }

    onCustomLocationAnalysisValues({ customLocationAnalysisValues }) {
        this.setState({
            customValues: customLocationAnalysisValues
                .filter(
                    customValue =>
                        customValue.analysisTypeUnit ===
                        this.state.currentAreaType.UNIT,
                )
                .map(customValue => customValue.value),
        });
    }

    /**
     * @param {number} value Radius size (in miles or minutes). Value is one of
     * the analysisType.VALUES_ARRAY
     */
    multiSelectionChange = value => {
        const newSelection = new Set(this.state.currentSelection);
        // At least one has to be selected and no more than the SELECTION_LIMIT
        switch (true) {
            case newSelection.has(value) && newSelection.size > 1:
                newSelection.delete(value);
                return newSelection;
            case !newSelection.has(value) && newSelection.size < SELECTION_LIMIT:
                newSelection.add(value);
                return newSelection;
            default:
                return this.state.currentSelection;
        }
    };

    onSelectionChange = value => {
        const newSelection = this.multiSelectionChange(value);

        this.setState({
            currentSelection: newSelection,
        });
    };

    onAreaTypeChange = value => {
        this.setState({
            currentAreaType: LocationAnalysisType[value],
            currentSelection: new Set(),
            customValues: [],
        });

        // wait for the state to update and then request custom values
        setTimeout(() => {
            this.emit('CUSTOM_LOCATION_ANALYSIS_VALUES_REQUEST');
        }, 0);
    };

    onApplySelection = () => {
        this.emit('SET_LOCATION_ANALYSIS_TYPE_AND_SELECTION', {
            mapInstanceId: this.props.mapInstanceId,
            analysisTypeId: this.state.currentAreaType.ID,
            selection: this.state.currentSelection,
        });

        this.bus.emit('LOCATION_ANALYSIS_MODE_STARTED', { mapInstanceId: this.props.mapInstanceId });
        this.props.onClose();
    }

    render() {
        const { mapInstanceId, onClose } = this.props;
        const { currentSelection, currentAreaType } = this.state;

        const values = [...currentAreaType.VALUES_ARRAY, ...this.state.customValues];
        const valueButtons = values
            .sort((a, b) => a - b)
            .map(value => (
                <LocationAnalysisValueButton
                    key={value}
                    value={value}
                    selection={currentSelection}
                    onSelectionChange={this.onSelectionChange}
                    analysisTypeUnit={currentAreaType.UNIT}
                />
            ));

        return (
            <div className="radius-selector">
                <div className="radius-selector__header">
                    <AreaTypeSelector
                        areaTypeId={currentAreaType.ID}
                        mapInstanceId={mapInstanceId}
                        onAreaTypeChange={this.onAreaTypeChange}
                    />
                    <button className="btn-icon radius-selector__modal-close" onClick={onClose}>
                        <i className="material-icons">close</i>
                    </button>
                </div>
                <div className="radius-selector__header-title">
                    <span>Select area (up to {SELECTION_LIMIT})</span>
                    <AreaSelectorInfoPopover />
                </div>
                <div className="radius-selector__content">
                    {valueButtons}
                    <AddNewCustomAnalysisValue
                        key="add-value-button"
                        values={values}
                        analysisType={currentAreaType}
                    />
                </div>
                <div className="radius-selector__actions">
                    <button
                        className="btn-link"
                        onClick={this.onApplySelection}
                        disabled={currentSelection.size === 0}
                    >
                        {this.props.intl.formatMessage({
                            id: 'apply',
                        })}
                    </button>
                </div>
            </div>
        );
    }
}

AreaSelectorPopup.propTypes = {
    analysisType: PropTypes.object.isRequired,
    mapInstanceId: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    intl: PropTypes.object.isRequired,
};

export default injectIntl(AreaSelectorPopup);
